import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import { api, unwrap } from '@/api/client';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { AvisoSinGuardar } from '@/components/PaginaEditor';

import { rutaClase, useClaseMutacion, type Clase, type PreguntaClase } from './queries';

const LETRAS = ['A', 'B', 'C', 'D'];

/** Caso clínico o quiz de la clase: uno por clase, la respuesta 1 es la correcta. */
export function PestanaPregunta({ c, tipo }: { c: Clase; tipo: 'case' | 'quiz' }) {
  const editable = useCan('contenido', 'editar');
  const actual = c[tipo];
  const inicial = {
    caseText: actual?.caseText ?? '',
    prompt: actual?.prompt ?? '',
    options: actual?.options ?? ['', '', '', ''],
    explanation: actual?.explanation ?? '',
  };
  const [f, setF] = useState(inicial);
  const [borrando, setBorrando] = useState(false);
  const sucio = JSON.stringify(f) !== JSON.stringify(inicial);
  const guardar = useClaseMutacion(
    c.id,
    (body: PreguntaClase) =>
      unwrap<Clase>(
        tipo === 'case'
          ? api.PUT('/api/v1/admin/classes/{id}/case', { ...rutaClase(c.id), body })
          : api.PUT('/api/v1/admin/classes/{id}/quiz', { ...rutaClase(c.id), body }),
      ),
    tipo === 'case' ? 'Caso clínico guardado.' : 'Quiz guardado.',
  );
  const borrar = useClaseMutacion(
    c.id,
    () =>
      unwrap<Clase>(
        tipo === 'case'
          ? api.DELETE('/api/v1/admin/classes/{id}/case', rutaClase(c.id))
          : api.DELETE('/api/v1/admin/classes/{id}/quiz', rutaClase(c.id)),
      ),
    'Borrado.',
  );
  return (
    <form
      className="panel-vidrio max-w-3xl space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate({
          ...(tipo === 'case' && { caseText: f.caseText.trim() || null }),
          prompt: f.prompt.trim(),
          options: f.options.map((o) => o.trim()),
          explanation: f.explanation.trim() || null,
        });
      }}
    >
      <AvisoSinGuardar sucio={sucio} />
      {actual && actual.answeredCount > 0 && (
        <p className="rounded-lg bg-[var(--pista)] p-3 text-sm">
          {actual.answeredCount} alumno(s) ya respondieron. Puedes corregir el texto, pero no
          cambies el sentido de las respuestas: se guarda la posición que eligió cada alumno.
        </p>
      )}
      <fieldset disabled={!editable} className="space-y-4">
        {tipo === 'case' && (
          <Campo etiqueta="Caso clínico">
            <textarea
              className="input-vidrio min-h-32"
              value={f.caseText}
              onChange={(e) => setF({ ...f, caseText: e.target.value })}
            />
          </Campo>
        )}
        <Campo etiqueta="Pregunta *">
          <textarea
            className="input-vidrio min-h-20"
            required
            minLength={3}
            value={f.prompt}
            onChange={(e) => setF({ ...f, prompt: e.target.value })}
          />
        </Campo>
        <div className="space-y-2">
          <span className="block text-sm font-bold text-texto-2">
            Respuestas (la A es la correcta; al alumno se le muestran en desorden)
          </span>
          {f.options.map((o, i) => (
            <label key={LETRAS[i]} className="flex items-center gap-2">
              <span
                className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-extrabold ${i === 0 ? 'bg-[var(--color-verde-600,#30a146)] text-white' : 'bg-[var(--pista)]'}`}
              >
                {LETRAS[i]}
              </span>
              <input
                className="input-vidrio"
                aria-label={`Respuesta ${LETRAS[i]}${i === 0 ? ' (correcta)' : ''}`}
                required
                maxLength={255}
                value={o}
                onChange={(e) =>
                  setF({ ...f, options: f.options.map((x, j) => (j === i ? e.target.value : x)) })
                }
              />
            </label>
          ))}
        </div>
        <Campo etiqueta="Justificación">
          <textarea
            className="input-vidrio min-h-24"
            value={f.explanation}
            onChange={(e) => setF({ ...f, explanation: e.target.value })}
          />
        </Campo>
      </fieldset>
      {editable && (
        <div className="flex justify-end gap-2">
          {actual && (
            <button
              type="button"
              className="btn-cs btn-vidrio mr-auto text-rojo-texto"
              onClick={() => setBorrando(true)}
            >
              <Trash2 size={16} aria-hidden /> Borrar
            </button>
          )}
          <button
            type="submit"
            className="btn-cs btn-primario"
            disabled={!sucio || guardar.isPending}
          >
            Guardar
          </button>
        </div>
      )}
      <ConfirmarAccion
        abierto={borrando}
        onCerrar={() => setBorrando(false)}
        titulo={tipo === 'case' ? 'Borrar el caso clínico' : 'Borrar el quiz'}
        descripcion="Si algún alumno ya respondió, no se puede borrar."
        textoBoton="Borrar"
        peligro
        ocupado={borrar.isPending}
        onConfirmar={() => borrar.mutate(undefined, { onSuccess: () => setBorrando(false) })}
      />
    </form>
  );
}
